import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Tabs,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Typography,
  Avatar,
  Dropdown,
  Divider,
  Table,
  Modal,
  message,
  Slider,
  Switch,
  Tree,
  Upload,
  List,
  Tag,
  Popconfirm
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  RobotOutlined,
  ApartmentOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  FolderOutlined,
  KeyOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CopyOutlined,
  CodeOutlined,
  PlayCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

export default function LabConfiguration() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { labId } = useParams();
  const [loading, setLoading] = useState(false);
  const [lab, setLab] = useState(null);
  const [aiCharacters, setAiCharacters] = useState([]);
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [labExercises, setLabExercises] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [form] = Form.useForm();
  const [criteriaForm] = Form.useForm();
  const [criteriaModalVisible, setCriteriaModalVisible] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState(null);
  const [exerciseForm] = Form.useForm();
  const [exerciseModalVisible, setExerciseModalVisible] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);

  // Avatar upload state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Lab materials state
  const [labMaterials, setLabMaterials] = useState([]);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);

  // External credentials state
  const [externalCredentials, setExternalCredentials] = useState([]);
  const [credentialModalVisible, setCredentialModalVisible] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [credentialForm] = Form.useForm();
  const [showPassword, setShowPassword] = useState({});

  // Character interactions state
  const [interactionScript, setInteractionScript] = useState('');
  const [interactionDescription, setInteractionDescription] = useState('');
  const [savingInteraction, setSavingInteraction] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeValidation, setCodeValidation] = useState(null);

  useEffect(() => {
    if (labId) {
      fetchLabConfiguration();
    }
  }, [labId]);

  const fetchLabConfiguration = async () => {
    try {
      setLoading(true);
      const [labRes, charactersRes, criteriaRes, exercisesRes, materialsRes, credentialsRes, interactionRes] = await Promise.allSettled([
        api.get(`/labs/${labId}`),
        api.get(`/labs/${labId}/ai-characters`),
        api.get(`/labs/${labId}/evaluation-criteria`),
        api.get(`/labs/${labId}/exercises`),
        api.get(`/labs/${labId}/materials`),
        api.get(`/labs/${labId}/credentials`),
        api.get(`/shared-knowledge/interactions/lab/${labId}`)
      ]);

      if (labRes.status === 'fulfilled') {
        setLab(labRes.value.data);
      }
      if (charactersRes.status === 'fulfilled') {
        setAiCharacters(charactersRes.value.data);
      }
      if (criteriaRes.status === 'fulfilled') {
        setEvaluationCriteria(criteriaRes.value.data);
      }
      if (exercisesRes.status === 'fulfilled') {
        setLabExercises(exercisesRes.value.data);
      }
      if (materialsRes.status === 'fulfilled') {
        setLabMaterials(materialsRes.value.data);
      }
      if (credentialsRes.status === 'fulfilled') {
        setExternalCredentials(credentialsRes.value.data);
      }
      if (interactionRes.status === 'fulfilled' && interactionRes.value.data.interaction_code) {
        setInteractionScript(interactionRes.value.data.interaction_code);
      }
    } catch (error) {
      message.error('Failed to load lab configuration');
      console.error('Error fetching lab configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  // AI Character Management
  const handleAddCharacter = () => {
    setEditingCharacter(null);
    form.resetFields();
    setAvatarFile(null);
    setAvatarPreview(null);
    setModalVisible(true);
  };

  const handleEditCharacter = (character) => {
    setEditingCharacter(character);
    form.setFieldsValue({
      ...character,
      response_delay_min: character.response_delay_min || 5,
      response_delay_max: character.response_delay_max || 30,
    });
    setAvatarFile(null);
    setAvatarPreview(character.avatar_url || null);
    setModalVisible(true);
  };

  const handleCharacterModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare data for API
      const characterData = {
        lab_id: parseInt(labId),
        name: values.name,
        role: values.role,
        system_prompt: values.system_prompt,
        response_delay_min: values.response_delay_min || 5,
        response_delay_max: values.response_delay_max || 30,
        is_active: values.is_active !== undefined ? values.is_active : true,
      };

      let characterId;

      if (editingCharacter) {
        await api.put(`/characters/${editingCharacter.id}`, characterData);
        characterId = editingCharacter.id;
        message.success('AI character updated successfully');
      } else {
        const response = await api.post(`/labs/${labId}/characters`, characterData);
        characterId = response.data.id;
        message.success('AI character created successfully');
      }

      // Upload avatar if file is selected
      if (avatarFile && characterId) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        await api.post(`/characters/${characterId}/avatar`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      setModalVisible(false);
      setAvatarFile(null);
      setAvatarPreview(null);
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to save AI character');
      console.error('Error saving AI character:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCharacter = async (characterId) => {
    try {
      setLoading(true);
      await api.delete(`/characters/${characterId}`);
      message.success('AI character deleted successfully');
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to delete AI character');
      console.error('Error deleting AI character:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateCharacter = async (characterId) => {
    try {
      setLoading(true);
      await api.post(`/characters/${characterId}/duplicate`);
      message.success('AI character duplicated successfully');
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to duplicate AI character');
      console.error('Error duplicating AI character:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (info) => {
    if (info.file.status === 'removed') {
      setAvatarFile(null);
      setAvatarPreview(null);
      return;
    }

    const file = info.file.originFileObj || info.file;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return;
    }

    // Validate file size (max 2MB)
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return;
    }

    setAvatarFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Evaluation Criteria Management
  const handleAddCriteria = () => {
    setEditingCriteria(null);
    criteriaForm.resetFields();
    setCriteriaModalVisible(true);
  };

  const handleEditCriteria = (criteria) => {
    setEditingCriteria(criteria);
    criteriaForm.setFieldsValue(criteria);
    setCriteriaModalVisible(true);
  };

  const handleCriteriaModalOk = async () => {
    try {
      const values = await criteriaForm.validateFields();
      setLoading(true);

      if (editingCriteria) {
        await api.put(`/labs/${labId}/evaluation-criteria/${editingCriteria.id}`, values);
        message.success('Evaluation criteria updated successfully');
      } else {
        await api.post(`/labs/${labId}/evaluation-criteria`, values);
        message.success('Evaluation criteria added successfully');
      }

      setCriteriaModalVisible(false);
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to save evaluation criteria');
      console.error('Error saving evaluation criteria:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCriteria = async (criteriaId) => {
    try {
      setLoading(true);
      await api.delete(`/labs/${labId}/evaluation-criteria/${criteriaId}`);
      message.success('Evaluation criteria deleted successfully');
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to delete evaluation criteria');
      console.error('Error deleting evaluation criteria:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lab Exercise Management
  const handleAddExercise = () => {
    setEditingExercise(null);
    exerciseForm.resetFields();
    setExerciseModalVisible(true);
  };

  const handleEditExercise = (exercise) => {
    setEditingExercise(exercise);
    exerciseForm.setFieldsValue(exercise);
    setExerciseModalVisible(true);
  };

  const handleExerciseModalOk = async () => {
    try {
      const values = await exerciseForm.validateFields();
      setLoading(true);

      if (editingExercise) {
        await api.put(`/labs/${labId}/exercises/${editingExercise.id}`, values);
        message.success('Lab exercise updated successfully');
      } else {
        await api.post(`/labs/${labId}/exercises`, values);
        message.success('Lab exercise added successfully');
      }

      setExerciseModalVisible(false);
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to save lab exercise');
      console.error('Error saving lab exercise:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    try {
      setLoading(true);
      await api.delete(`/labs/${labId}/exercises/${exerciseId}`);
      message.success('Lab exercise deleted successfully');
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to delete lab exercise');
      console.error('Error deleting lab exercise:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lab Materials Management
  const handleUploadMaterial = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);
    formData.append('material_type', 'file');

    try {
      setUploadingMaterial(true);
      await api.post(`/labs/${labId}/materials`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      message.success('Material uploaded successfully');
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to upload material');
      console.error('Error uploading material:', error);
    } finally {
      setUploadingMaterial(false);
    }
    return false; // Prevent default upload behavior
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await api.delete(`/labs/${labId}/materials/${materialId}`);
      message.success('Material deleted successfully');
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to delete material');
      console.error('Error deleting material:', error);
    }
  };

  const handleDownloadMaterial = async (materialId, filename) => {
    try {
      const response = await api.get(`/labs/${labId}/materials/${materialId}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error('Failed to download material');
      console.error('Error downloading material:', error);
    }
  };

  // External Credentials Management
  const handleAddCredential = () => {
    setEditingCredential(null);
    credentialForm.resetFields();
    setCredentialModalVisible(true);
  };

  const handleEditCredential = (credential) => {
    setEditingCredential(credential);
    credentialForm.setFieldsValue({
      ...credential,
      password: '', // Don't populate password for security
    });
    setCredentialModalVisible(true);
  };

  const handleCredentialModalOk = async () => {
    try {
      const values = await credentialForm.validateFields();
      setLoading(true);

      if (editingCredential) {
        // Only send password if it was changed
        const updateData = { ...values };
        if (!updateData.password) {
          delete updateData.password;
        }
        await api.put(`/labs/${labId}/credentials/${editingCredential.id}`, updateData);
        message.success('Credential updated successfully');
      } else {
        await api.post(`/labs/${labId}/credentials`, values);
        message.success('Credential added successfully');
      }

      setCredentialModalVisible(false);
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to save credential');
      console.error('Error saving credential:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    try {
      setLoading(true);
      await api.delete(`/labs/${labId}/credentials/${credentialId}`);
      message.success('Credential deleted successfully');
      fetchLabConfiguration();
    } catch (error) {
      message.error('Failed to delete credential');
      console.error('Error deleting credential:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (credentialId) => {
    setShowPassword(prev => ({
      ...prev,
      [credentialId]: !prev[credentialId]
    }));
  };

  // Character interaction handlers
  const handleValidateInteractionCode = async () => {
    if (!interactionScript || interactionScript.trim() === '') {
      message.warning('Please enter JavaScript code to validate');
      return;
    }

    try {
      setValidatingCode(true);
      const response = await api.post('/shared-knowledge/interactions/validate', {
        interaction_code: interactionScript
      });

      setCodeValidation(response.data);
      if (response.data.valid) {
        message.success('JavaScript code is valid!');
      } else {
        message.error(`Validation failed: ${response.data.message}`);
      }
    } catch (error) {
      message.error('Failed to validate code');
      console.error('Error validating code:', error);
      setCodeValidation({ valid: false, message: 'Validation request failed' });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleSaveInteractionScript = async () => {
    if (!interactionScript || interactionScript.trim() === '') {
      message.warning('Please enter JavaScript code before saving');
      return;
    }

    try {
      setSavingInteraction(true);
      await api.post(`/shared-knowledge/interactions/lab/${labId}`, {
        interaction_code: interactionScript,
        description: interactionDescription,
        is_active: true
      });

      message.success('Character interaction script saved successfully!');
    } catch (error) {
      message.error('Failed to save interaction script');
      console.error('Error saving interaction script:', error);
    } finally {
      setSavingInteraction(false);
    }
  };

  const characterColumns = [
    {
      title: 'Avatar',
      dataIndex: 'avatar_url',
      key: 'avatar',
      width: 80,
      render: (url) => (
        <Avatar
          src={url}
          icon={<RobotOutlined />}
          size="large"
        />
      ),
    },
    {
      title: 'Character Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: 'Response Delay (s)',
      key: 'response_delay',
      render: (_, record) => `${record.response_delay_min || 5}-${record.response_delay_max || 30}s`
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCharacter(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={() => handleDuplicateCharacter(record.id)}
          >
            Duplicate
          </Button>
          <Popconfirm
            title="Delete this AI character?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteCharacter(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const criteriaColumns = [
    {
      title: 'Order',
      dataIndex: 'evaluation_order',
      key: 'evaluation_order',
      width: 80,
      sorter: (a, b) => a.evaluation_order - b.evaluation_order,
    },
    {
      title: 'Type',
      dataIndex: 'criteria_type',
      key: 'criteria_type',
      width: 180,
      render: (type) => {
        const typeLabels = {
          task_completion: 'Task Completion',
          timing: 'Timing',
          communication_quality: 'Communication',
          escalation: 'Escalation',
          custom: 'Custom'
        };
        return typeLabels[type] || type;
      }
    },
    {
      title: 'Description',
      dataIndex: 'criteria_description',
      key: 'criteria_description',
      ellipsis: true,
    },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
      render: (weight) => `${(parseFloat(weight) * 100).toFixed(0)}%`
    },
    {
      title: 'Required',
      dataIndex: 'is_required',
      key: 'is_required',
      width: 100,
      render: (required) => required ? 'Yes' : 'No'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCriteria(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCriteria(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const exerciseColumns = [
    {
      title: 'Exercise Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => type || 'Task',
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditExercise(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteExercise(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const tabs = [
    {
      key: '1',
      label: (
        <span>
          <RobotOutlined />
          AI Characters
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCharacter}
            >
              Add AI Character
            </Button>
          </div>
          <Table
            columns={characterColumns}
            dataSource={aiCharacters}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
      ),
    },
    {
      key: '2',
      label: (
        <span>
          <ApartmentOutlined />
          Monitoring Hierarchy
        </span>
      ),
      children: (
        <Card>
          <Text type="secondary">
            Configure monitoring systems and hierarchy for this lab.
          </Text>
          <Divider />
          <p style={{ color: '#999' }}>Monitoring hierarchy builder - Coming soon</p>
        </Card>
      ),
    },
    {
      key: '3',
      label: (
        <span>
          <CheckSquareOutlined />
          Evaluation Criteria
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCriteria}
            >
              Add Evaluation Criteria
            </Button>
          </div>
          <Table
            columns={criteriaColumns}
            dataSource={evaluationCriteria}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
      ),
    },
    {
      key: '4',
      label: (
        <span>
          <FileTextOutlined />
          Lab Exercises
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddExercise}
            >
              Add Lab Exercise
            </Button>
          </div>
          <Table
            columns={exerciseColumns}
            dataSource={labExercises}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
      ),
    },
    {
      key: '5',
      label: (
        <span>
          <FolderOutlined />
          Lab Materials
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Upload
              beforeUpload={handleUploadMaterial}
              showUploadList={false}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={uploadingMaterial}
              >
                Upload Material
              </Button>
            </Upload>
          </div>
          <List
            dataSource={labMaterials}
            loading={loading}
            locale={{ emptyText: 'No materials uploaded yet' }}
            renderItem={(material) => (
              <List.Item
                key={material.id}
                actions={[
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadMaterial(material.id, material.title)}
                  >
                    Download
                  </Button>,
                  <Popconfirm
                    title="Delete this material?"
                    onConfirm={() => handleDeleteMaterial(material.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={material.title}
                  description={
                    <Space>
                      <Tag>{material.material_type}</Tag>
                      {material.file_size && (
                        <Text type="secondary">
                          {(material.file_size / 1024).toFixed(2)} KB
                        </Text>
                      )}
                      <Text type="secondary">
                        Uploaded: {new Date(material.created_at).toLocaleDateString()}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      ),
    },
    {
      key: '6',
      label: (
        <span>
          <KeyOutlined />
          External Credentials
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCredential}
            >
              Add Credential
            </Button>
          </div>
          <List
            dataSource={externalCredentials}
            loading={loading}
            locale={{ emptyText: 'No credentials configured yet' }}
            renderItem={(credential) => (
              <List.Item
                key={credential.id}
                actions={[
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEditCredential(credential)}
                  >
                    Edit
                  </Button>,
                  <Popconfirm
                    title="Delete this credential?"
                    onConfirm={() => handleDeleteCredential(credential.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={credential.service_name}
                  description={
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>URL:</Text> {credential.service_url}
                      </div>
                      <div>
                        <Text strong>Username:</Text> {credential.username}
                      </div>
                      <div>
                        <Text strong>Password:</Text>{' '}
                        {showPassword[credential.id] ? (
                          <>
                            {credential.password}{' '}
                            <Button
                              type="link"
                              size="small"
                              icon={<EyeInvisibleOutlined />}
                              onClick={() => togglePasswordVisibility(credential.id)}
                            />
                          </>
                        ) : (
                          <>
                            ••••••••{' '}
                            <Button
                              type="link"
                              size="small"
                              icon={<EyeOutlined />}
                              onClick={() => togglePasswordVisibility(credential.id)}
                            />
                          </>
                        )}
                      </div>
                      {credential.notes && (
                        <div>
                          <Text strong>Notes:</Text> {credential.notes}
                        </div>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      ),
    },
    {
      key: '7',
      label: (
        <span>
          <CodeOutlined />
          Character Interactions
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Title level={4}>JavaScript Interaction Logic</Title>
            <Text type="secondary">
              Define custom JavaScript code to control how AI characters coordinate using shared knowledge.
              This code executes in a sandbox when messages are sent.
            </Text>
          </div>

          <Divider />

          <Form layout="vertical">
            <Form.Item label="Description (optional)">
              <Input.TextArea
                value={interactionDescription}
                onChange={(e) => setInteractionDescription(e.target.value)}
                placeholder="Describe what this interaction logic does..."
                rows={2}
              />
            </Form.Item>

            <Form.Item
              label="JavaScript Code"
              help={
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Available functions: <code>setSharedKnowledge(key, value)</code>, <code>getSharedKnowledge(key)</code>,
                    <code>deleteSharedKnowledge(key)</code>, <code>log(message)</code>
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Context variables: <code>context.character_name</code>, <code>context.message</code>,
                    <code>context.shared_knowledge</code>
                  </Text>
                </div>
              }
            >
              <Input.TextArea
                value={interactionScript}
                onChange={(e) => setInteractionScript(e.target.value)}
                placeholder={`// Example interaction logic:
if (context.message.includes('server')) {
  setSharedKnowledge('server_mentioned', 'true');
  log('Server was mentioned in conversation');
}

if (context.character_name === 'IT Manager') {
  var diskIssue = getSharedKnowledge('disk_space_issue');
  if (diskIssue) {
    setSharedKnowledge('manager_notified', 'true');
  }
}`}
                rows={16}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            </Form.Item>

            {codeValidation && (
              <Form.Item>
                {codeValidation.valid ? (
                  <div style={{ padding: 12, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                    <Text type="success">
                      <CheckSquareOutlined /> Code is valid and ready to save
                    </Text>
                  </div>
                ) : (
                  <div style={{ padding: 12, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4 }}>
                    <Text type="danger">
                      <CloseCircleOutlined /> {codeValidation.message}
                    </Text>
                  </div>
                )}
              </Form.Item>
            )}

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveInteractionScript}
                  loading={savingInteraction}
                  disabled={validatingCode}
                >
                  Save Interaction Script
                </Button>
                <Button
                  icon={<PlayCircleOutlined />}
                  onClick={handleValidateInteractionCode}
                  loading={validatingCode}
                  disabled={savingInteraction}
                >
                  Validate Code
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/admin/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Lab Configuration</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>Lab Configuration</Title>
          <Text type="secondary">
            {lab ? `Configuring: ${lab.name}` : 'Configure AI characters, monitoring, and evaluation'}
          </Text>
        </div>

        <Tabs items={tabs} defaultActiveKey="1" />
      </Content>

      {/* AI Character Modal */}
      <Modal
        title={editingCharacter ? 'Edit AI Character' : 'Add AI Character'}
        open={modalVisible}
        onOk={handleCharacterModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          {/* Avatar Upload */}
          <Form.Item label="Character Avatar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Avatar
                src={avatarPreview}
                icon={<RobotOutlined />}
                size={80}
              />
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleAvatarChange}
              >
                <Button icon={<UploadOutlined />}>
                  {avatarPreview ? 'Change Avatar' : 'Upload Avatar'}
                </Button>
              </Upload>
              {avatarPreview && (
                <Button
                  danger
                  onClick={() => {
                    setAvatarFile(null);
                    setAvatarPreview(null);
                  }}
                >
                  Remove
                </Button>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Recommended: Square image, max 2MB
            </Text>
          </Form.Item>

          <Divider />

          {/* Basic Information */}
          <Form.Item
            name="name"
            label="Character Name"
            rules={[{ required: true, message: 'Please enter character name' }]}
          >
            <Input
              placeholder="e.g., John Smith"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="role"
            label="Character Role/Title"
            rules={[{ required: true, message: 'Please enter character role' }]}
          >
            <Input
              placeholder="e.g., Senior Network Administrator"
              size="large"
            />
          </Form.Item>

          {/* System Prompt Editor */}
          <Form.Item
            name="system_prompt"
            label={
              <span>
                System Prompt
                <Text type="secondary" style={{ marginLeft: 8, fontWeight: 'normal', fontSize: 12 }}>
                  Define the character's personality, knowledge, expertise, and communication style
                </Text>
              </span>
            }
            rules={[{ required: true, message: 'Please enter system prompt' }]}
          >
            <TextArea
              rows={10}
              placeholder={`Example:
You are John Smith, a Senior Network Administrator with 15 years of experience...

Personality:
- Professional but friendly
- Patient with junior staff
- Detail-oriented

Knowledge Areas:
- Cisco routing and switching
- Network security
- Troubleshooting methodologies

Communication Style:
- Uses technical terminology appropriately
- Provides clear explanations
- Offers helpful suggestions`}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>

          <Divider />

          {/* Response Delay Configuration */}
          <Form.Item label="Response Delay Range (seconds)">
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              Configure how long this character takes to respond (simulates realistic thinking time)
            </Text>
            <div style={{ padding: '0 16px' }}>
              <Form.Item
                name="response_delay_min"
                label="Minimum Delay"
                initialValue={5}
              >
                <Slider
                  min={0}
                  max={60}
                  marks={{
                    0: '0s',
                    5: '5s',
                    15: '15s',
                    30: '30s',
                    60: '60s'
                  }}
                  tooltip={{ formatter: (value) => `${value}s` }}
                />
              </Form.Item>

              <Form.Item
                name="response_delay_max"
                label="Maximum Delay"
                initialValue={30}
              >
                <Slider
                  min={0}
                  max={120}
                  marks={{
                    0: '0s',
                    15: '15s',
                    30: '30s',
                    60: '60s',
                    120: '120s'
                  }}
                  tooltip={{ formatter: (value) => `${value}s` }}
                />
              </Form.Item>
            </div>
          </Form.Item>

          <Divider />

          {/* Status */}
          <Form.Item
            name="is_active"
            label="Character Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Evaluation Criteria Modal */}
      <Modal
        title={editingCriteria ? 'Edit Evaluation Criteria' : 'Add Evaluation Criteria'}
        open={criteriaModalVisible}
        onOk={handleCriteriaModalOk}
        onCancel={() => setCriteriaModalVisible(false)}
        width={800}
        confirmLoading={loading}
      >
        <Form form={criteriaForm} layout="vertical">
          <Form.Item
            name="lab_id"
            hidden
            initialValue={labId}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="evaluation_order"
            label="Evaluation Order"
            rules={[{ required: true, message: 'Please enter evaluation order' }]}
            initialValue={evaluationCriteria.length + 1}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="criteria_type"
            label="Criteria Type"
            rules={[{ required: true, message: 'Please select criteria type' }]}
          >
            <Select placeholder="Select criteria type">
              <Select.Option value="task_completion">Task Completion</Select.Option>
              <Select.Option value="timing">Timing</Select.Option>
              <Select.Option value="communication_quality">Communication Quality</Select.Option>
              <Select.Option value="escalation">Escalation</Select.Option>
              <Select.Option value="custom">Custom</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="criteria_description"
            label="Criteria Description"
            rules={[{ required: true, message: 'Please enter criteria description' }]}
          >
            <TextArea
              rows={3}
              placeholder="e.g., Student should properly diagnose the network connectivity issue"
            />
          </Form.Item>

          <Form.Item
            name="ai_evaluation_prompt"
            label="AI Evaluation Prompt"
            rules={[{ required: true, message: 'Please enter AI evaluation prompt' }]}
            tooltip="Detailed instructions for the AI on how to evaluate this criterion"
          >
            <TextArea
              rows={6}
              placeholder="Review the conversation and determine if the student successfully identified the root cause of the network issue. Look for evidence that they checked DNS settings, verified network configuration, and asked appropriate troubleshooting questions."
            />
          </Form.Item>

          <Form.Item
            name="target_character_id"
            label="Target AI Character (Optional)"
            tooltip="If this criteria only applies to conversations with a specific character"
          >
            <Select placeholder="Select AI character" allowClear>
              {aiCharacters.map(char => (
                <Select.Option key={char.id} value={char.id}>
                  {char.name} ({char.role})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="expected_action"
            label="Expected Action (Optional)"
            tooltip="What specific action should the student take?"
          >
            <TextArea
              rows={2}
              placeholder="e.g., Run 'ipconfig /all' and verify DNS server settings"
            />
          </Form.Item>

          <Form.Item
            name="timing_requirement_minutes"
            label="Time Requirement (Minutes - Optional)"
            tooltip="Maximum time allowed to complete this task"
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="e.g., 15" />
          </Form.Item>

          <Form.Item
            name="weight"
            label="Weight (0.0 to 1.0)"
            rules={[
              { required: true, message: 'Please enter weight' },
              {
                validator: (_, value) => {
                  if (value >= 0 && value <= 1) return Promise.resolve();
                  return Promise.reject(new Error('Weight must be between 0.0 and 1.0'));
                }
              }
            ]}
            tooltip="Importance of this criterion in overall evaluation (0.0-1.0)"
          >
            <InputNumber
              min={0}
              max={1}
              step={0.1}
              style={{ width: '100%' }}
              placeholder="e.g., 0.3 for 30% of total score"
            />
          </Form.Item>

          <Form.Item
            name="is_required"
            label="Required Criterion"
            valuePropName="checked"
            tooltip="Must pass this criterion to complete the lab successfully"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Lab Exercise Modal */}
      <Modal
        title={editingExercise ? 'Edit Lab Exercise' : 'Add Lab Exercise'}
        open={exerciseModalVisible}
        onOk={handleExerciseModalOk}
        onCancel={() => setExerciseModalVisible(false)}
        width={700}
        confirmLoading={loading}
      >
        <Form form={exerciseForm} layout="vertical">
          <Form.Item
            name="name"
            label="Exercise Name"
            rules={[{ required: true, message: 'Please enter exercise name' }]}
          >
            <Input placeholder="e.g., Configure Network Firewall" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              rows={4}
              placeholder="Describe the exercise objectives and requirements..."
            />
          </Form.Item>

          <Form.Item
            name="instructions"
            label="Instructions"
            rules={[{ required: true, message: 'Please enter instructions' }]}
          >
            <TextArea
              rows={6}
              placeholder="Step-by-step instructions for completing the exercise..."
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Exercise Type"
            rules={[{ required: true, message: 'Please select exercise type' }]}
          >
            <Select placeholder="Select exercise type">
              <Select.Option value="task">Task</Select.Option>
              <Select.Option value="scenario">Scenario</Select.Option>
              <Select.Option value="troubleshooting">Troubleshooting</Select.Option>
              <Select.Option value="configuration">Configuration</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="order"
            label="Order"
            rules={[{ required: true, message: 'Please enter exercise order' }]}
          >
            <InputNumber min={1} placeholder="Exercise order in the lab" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="expected_duration"
            label="Expected Duration (minutes)"
          >
            <InputNumber min={5} max={480} placeholder="Expected time to complete" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="points"
            label="Points"
          >
            <InputNumber min={0} max={100} placeholder="Points for this exercise" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* External Credentials Modal */}
      <Modal
        title={editingCredential ? 'Edit External Credential' : 'Add External Credential'}
        open={credentialModalVisible}
        onOk={handleCredentialModalOk}
        onCancel={() => setCredentialModalVisible(false)}
        width={600}
        confirmLoading={loading}
      >
        <Form form={credentialForm} layout="vertical">
          <Form.Item
            name="service_name"
            label="Service Name"
            rules={[{ required: true, message: 'Please enter service name' }]}
          >
            <Input placeholder="e.g., Production Database" />
          </Form.Item>

          <Form.Item
            name="service_url"
            label="Service URL"
            rules={[{ required: true, message: 'Please enter service URL' }]}
          >
            <Input placeholder="e.g., https://db.example.com:5432" />
          </Form.Item>

          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Please enter username' }]}
          >
            <Input placeholder="e.g., admin" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={editingCredential ? [] : [{ required: true, message: 'Please enter password' }]}
            extra={editingCredential ? 'Leave blank to keep existing password' : ''}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes (Optional)"
          >
            <TextArea
              rows={3}
              placeholder="Additional information about this credential..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
